<template>
  <!-- ── The title, and the country under its authority ──────────────────
       The country rides in the title because it is the one thing that makes
       these numbers *yours* rather than a reference table, and it belongs
       where a reader looks first. A middot, not an em dash: the protocol
       names below already carry one ("BSH — United Kingdom"), and two dashes
       in two stacked lines read as one broken sentence.

       Padding is the host's to set, not this component's: the dialog gutters
       at 24px and the settings card at 16px, and a header whose title does
       not sit on the same left edge as the content under it reads as a
       banner pasted on top of the card rather than part of it. -->
  <div class="clinical-header d-flex align-center ga-3" :class="{ 'clinical-header-plain': plain }">
    <!-- The same glyph the Clinical tab carries, so the dialog, the tab and
         the card it hands off to are visibly the same subject — same icon and
         same weight, and neither half is a free choice.

         This app's Font Awesome kit serves a subset per icon AND per weight, so
         an unsubsetted pairing renders as the placeholder rather than falling
         back to another weight: `notes-medical` is absent in every style, and
         `fa-light fa-clipboard-medical` — which stood here in regular — drew
         nothing even though ~50 other light icons in this app are fine. This
         pairing was added to the kit deliberately. Confirm the glyph actually
         draws before changing either half; see AccountTabsBar.vue and
         drawer.vue for the same class of kit gap. -->
    <v-icon v-if="!plain" size="20" class="clinical-header-icon" aria-hidden="true">fa-light fa-file-medical</v-icon>
    <h2 class="clinical-header-title flex-grow-1">
      {{ title
      }}<span v-if="countryName" class="clinical-header-country"> · {{ countryName }}</span>
    </h2>

    <!-- Whatever acts on the card as a whole, at the far end of the line that
         names it: the disclaimer a pharmacist may have to forward, and the
         citation panel behind the info glyph. Not the protocol's own controls
         — those belong on the line carrying the protocol's name. -->
    <div v-if="$slots.actions" class="d-flex align-center ga-1 flex-shrink-0">
      <slot name="actions" />
    </div>
  </div>
</template>

<script setup>
/**
 * The title over a set of clinical limits — a blue bar on the first-run
 * dialog, the page's own heading on the settings card (`plain`).
 *
 * One component, two screens: the first-run dialog that ASKS a pharmacy to
 * accept a protocol and the settings card that lets them change it are the
 * same subject, and a reader who met one should recognise the other. It was
 * written twice before, and the copies had already begun to differ.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  /** The heading itself. Already translated — this component picks no string. */
  title: { type: String, required: true },
  /** The account's country, as `useClinicalProtocol().jurisdiction` gives it. */
  jurisdiction: { type: Object, default: null },
  /**
   * Drop the chrome: no glyph, no blue, the page's own ink.
   *
   * The dialog is a gate — it arrives over the app unasked, and the bar is
   * what says so. The settings card is not: a pharmacist navigated to it,
   * under a tab already carrying that glyph and that name, so the same bar
   * there announces a second time what nobody needed telling once.
   */
  plain: { type: Boolean, default: false }
})

const { locale } = useI18n()

/**
 * The country in the reader's language. The server ships the English display
 * name, which put "Aucun protocole pour Germany" in front of a French
 * pharmacist; `Intl.DisplayNames` needs no locale files of its own.
 */
const countryName = computed(() => {
  const country = props.jurisdiction
  if (!country) return ''
  try {
    const localised = new Intl.DisplayNames([locale.value || 'en'], { type: 'region' }).of(country.code)
    if (localised && localised !== country.code) return localised
  } catch {
    // An unusable code, or a browser without the API: fall through to the name.
  }
  return country.name || country.code || ''
})
</script>

<style scoped>
.clinical-header {
  background: #2563eb;
}

/* Text, not chrome. The colour is the theme's own ink rather than a literal
   black: this app has a dark mode, and #000 on a dark card is a title nobody
   can read. */
.clinical-header-plain {
  background: none;
}

.clinical-header-plain .clinical-header-title {
  color: rgb(var(--v-theme-on-surface));
}

.clinical-header-plain .clinical-header-country {
  color: rgba(var(--v-theme-on-surface), 0.6);
}

/* Optically aligned with the title's cap height rather than its line box, so
   the glyph does not sit low against a bold 17px line. */
.clinical-header-icon {
  color: rgba(255, 255, 255, 0.9);
  margin-top: 1px;
}

.clinical-header-title {
  color: #fff;
  font-size: 1.0625rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.3;
  margin: 0;
  min-width: 0;
}

.clinical-header-country {
  color: rgba(255, 255, 255, 0.7);
  font-weight: 400;
}
</style>
